/**
 * Asking a grid view for the numbers under its columns.
 *
 * Server state, so an ordinary TanStack Query -- unlike the rows, which are a
 * window. What is unusual is the key: it carries the *membership* half of the
 * row query's signature rather than the whole of it. An aggregation over a set
 * does not care what order the set is in, so sorting or grouping a view leaves
 * every number under it exactly where it was, and a key built from the whole
 * signature would send the footer off to ask again for an answer that cannot
 * have moved. Filtering or searching does change the set, and does ask again.
 *
 * Keyed rather than invalidated, so switching a filter off and on again shows
 * the numbers immediately instead of blanking them for a round trip.
 *
 * **The endpoint is not told which aggregations to compute.** It reads what
 * each field's options have stored -- `aggregation_raw_type` -- so the request
 * carries nothing but the rows to compute over. That is also why changing a
 * column's aggregation has to invalidate this: the question the server will
 * answer next time is a different one.
 */
import { queryOptions, useQuery } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { RowSearch } from '../rows/rowQuery'

export interface ViewAggregations {
  /** The raw answer per field, keyed by its `field_{id}` column name. */
  values: Record<string, unknown>
  /**
   * How many rows the aggregations were taken over.
   *
   * From the same response, deliberately, rather than from the row window's
   * count: a percentage worked out against a row count taken at a different
   * moment is wrong by however much the table moved in between.
   */
  rowCount: number
}

/** What the endpoint answers with, narrowed to what is read. */
type AggregationsResponse = Record<string, unknown> & { total?: number }

export function viewAggregationsQuery(
  viewId: number,
  signature: string,
  search: RowSearch,
) {
  return queryOptions({
    queryKey: qk.aggregations(viewId, signature),
    queryFn: async (): Promise<ViewAggregations> => {
      const body = (await unwrap(
        getApi().GET('/api/database/views/grid/{view_id}/aggregations/', {
          params: {
            path: { view_id: viewId },
            query: {
              // Without it there is no row count in the answer, and four of the
              // nineteen aggregations are a count divided by one.
              include: 'total',
              ...(search.term === ''
                ? {}
                : { search: search.term, search_mode: search.mode }),
            },
          },
        }),
      )) as AggregationsResponse

      const { total, ...values } = body
      return { values, rowCount: total ?? 0 }
    },
  })
}

/**
 * The numbers for one view, asked for only when something is showing one.
 *
 * A view with no aggregations set answers with the row count and nothing else,
 * so asking is a request per table that nobody reads.
 */
export function useViewAggregations(
  viewId: number,
  signature: string,
  search: RowSearch,
  { enabled }: { enabled: boolean },
) {
  return useQuery({ ...viewAggregationsQuery(viewId, signature, search), enabled })
}

/**
 * The same numbers on a publicly shared view.
 *
 * A different endpoint and the same everything else: the aggregations are the
 * owner's choice, stored in the view's field options, and a reader is told what
 * they add up to without being able to change what is added up. Addressed by
 * slug, and carrying no credential, exactly as the public rows fetcher is.
 */
export function publicViewAggregationsQuery(
  slug: string,
  signature: string,
  search: RowSearch,
) {
  return queryOptions({
    queryKey: qk.publicAggregations(slug, signature),
    queryFn: async (): Promise<ViewAggregations> => {
      const body = (await unwrap(
        getApi().GET('/api/database/views/grid/{slug}/public/aggregations/', {
          params: {
            path: { slug },
            query: {
              include: 'total',
              ...(search.term === ''
                ? {}
                : { search: search.term, search_mode: search.mode }),
            },
          },
        }),
      )) as AggregationsResponse

      const { total, ...values } = body
      return { values, rowCount: total ?? 0 }
    },
  })
}

export function usePublicViewAggregations(
  slug: string,
  signature: string,
  search: RowSearch,
  { enabled }: { enabled: boolean },
) {
  return useQuery({
    ...publicViewAggregationsQuery(slug, signature, search),
    enabled,
  })
}
